import { request } from "./client";

export type ExecutiveSummary = {
  total_active_threads: number;
  stalled_threads: number;
  pending_hitl: number;
  overdue_hitl: number;
  delegations_in_flight: number;
  pending_podcast_reviews?: number;
  active_podcast_productions?: number;
};

export type StalledThread = {
  thread_id: string;
  agent: string;
  reasons: string[];
};

export type HitlTimeout = {
  id?: string;
  tool_id?: string;
  fn_name?: string;
  payload?: Record<string, unknown> | null;
  agent?: string;
  age_sec?: number;
};

export type ExecutiveGoal = {
  id: string;
  title: string;
  status: string;
  deadline?: string;
  agent?: string;
};

export type FrontendErrorSummary = {
  last24h: number;
  uniqueUsers: number;
  severity: Record<string, number>;
  versions: Record<string, number>;
};

export type VersionHealthStatus = {
  version: string;
  status: "healthy" | "warning" | "frozen";
  errors_last_10m: number;
  is_frozen: boolean;
  evaluated_at: string;
};

export async function fetchExecutiveSummary(): Promise<ExecutiveSummary> {
  return request<ExecutiveSummary>("/executive/summary");
}

export async function fetchStalledThreads(): Promise<StalledThread[]> {
  const payload = await request<{ stalled: StalledThread[] }>("/executive/stalled");
  return payload?.stalled ?? [];
}

export async function fetchHitlTimeouts(): Promise<HitlTimeout[]> {
  const payload = await request<{ overdue: HitlTimeout[] }>("/executive/hitl/timeouts");
  return payload?.overdue ?? [];
}

export type CapabilityDebugPayload = {
  tier?: string | null;
  organization_tier?: string | null;
  capabilities?: Record<string, boolean>;
  credits?: number | null;
  credit_expires_at?: string | null;
  limits?: Record<string, unknown> | null;
};

export type ExecutiveGoalsResponse = {
  goals: ExecutiveGoal[];
  capability_debug?: CapabilityDebugPayload;
};

export async function fetchExecutiveGoals(): Promise<ExecutiveGoalsResponse> {
  const payload = await request<ExecutiveGoalsResponse>("/executive/goals");
  return {
    goals: payload?.goals ?? [],
    capability_debug: payload?.capability_debug,
  };
}

export async function fetchFrontendErrorSummary(): Promise<FrontendErrorSummary> {
  return request<FrontendErrorSummary>("/executive/observability/frontend/summary");
}

export async function fetchVersionHealthStatus(): Promise<VersionHealthStatus | null> {
  try {
    return await request<VersionHealthStatus>("/observability/frontend/version-status");
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (message.includes("404")) {
      return null;
    }
    throw error;
  }
}

export type MasterControlUser = {
  id: string;
  name?: string | null;
  email?: string | null;
  role?: string | null;
  avatar_url?: string | null;
  organization_id?: string | null;
  organization_name?: string | null;
  organization?: { id?: string; name?: string | null; billing_exempt?: boolean | null } | null;
  organization_billing_exempt?: boolean | null;
  user_billing_exempt?: boolean | null;
  last_seen_at?: string | null;
  last_login_at?: string | null;
  agent_actions_24h?: number | null;
  agent_actions_total?: number | null;
  status?: string | null;
  [key: string]: unknown;
};

export type MasterControlOrganization = {
  id: string;
  name?: string | null;
  member_count?: number | null;
  subscription_tier?: string | null;
  seat_limit?: number | null;
  stripe_subscription_status?: string | null;
  created_at?: string | null;
  last_activity_at?: string | null;
  status?: string | null;
  [key: string]: unknown;
};

export type BillingExemptPayload = {
  org_id?: string | null;
  user_id?: string | null;
  billing_exempt: boolean;
};

export type AgentUsageStats = {
  agent: string;
  last_24h_runs?: number | null;
  total_runs?: number | null;
  error_count?: number | null;
  avg_duration_ms?: number | null;
  [key: string]: unknown;
};

export type AgentUsageMap = Record<string, AgentUsageStats>;

export type ExecutiveMetrics = {
  total_users?: number | null;
  total_organizations?: number | null;
  dau?: number | null;
  mau?: number | null;
  daily_active_users?: number | null;
  monthly_active_users?: number | null;
  active_organizations?: number | null;
  agent_actions_24h?: number | null;
  agent_actions_last_24h?: number | null;
  revenue_placeholder?: number | null;
  [key: string]: unknown;
} | null;

export type ExecutiveActivityEvent = {
  id?: string;
  type?: string;
  event?: string;
  source?: string;
  service?: string;
  message?: string;
  level?: string;
  timestamp?: string | null;
  created_at?: string | null;
  payload?: Record<string, unknown>;
  [key: string]: unknown;
};

export type ExecutiveAIUsageTotals = {
  requests: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost_usd: number;
};

export type ExecutiveAIUsageEntry = {
  org_id?: string | null;
  org_name?: string | null;
  user_id?: string | null;
  user_email?: string | null;
  requests: number;
  total_tokens: number;
  cost_usd: number;
};

export type ExecutiveAIUsage = {
  window_days: number;
  totals: ExecutiveAIUsageTotals;
  orgs: ExecutiveAIUsageEntry[];
  users: ExecutiveAIUsageEntry[];
};

export async function fetchExecutiveAIUsage(windowDays = 30): Promise<ExecutiveAIUsage> {
  return request<ExecutiveAIUsage>(`/executive/ai-usage?window_days=${windowDays}`);
}

type SystemUsersResponse = { users?: MasterControlUser[] } | MasterControlUser[] | null;

export async function fetchSystemUsers(): Promise<MasterControlUser[]> {
  const payload = await request<SystemUsersResponse>("/executive/system-users");
  if (Array.isArray(payload)) return payload;
  if (payload?.users && Array.isArray(payload.users)) return payload.users;
  return [];
}

type OrganizationsResponse = { organizations?: MasterControlOrganization[] } | MasterControlOrganization[] | null;

export async function fetchExecutiveOrganizations(): Promise<MasterControlOrganization[]> {
  const payload = await request<OrganizationsResponse>("/executive/organizations");
  if (Array.isArray(payload)) return payload;
  if (payload?.organizations && Array.isArray(payload.organizations)) {
    return payload.organizations;
  }
  return [];
}

export async function setBillingExempt(payload: BillingExemptPayload): Promise<{ billing_exempt: boolean }> {
  return request<{ billing_exempt: boolean }>("/master-control/billing-exempt", {
    method: "POST",
    body: payload,
  });
}

type AgentUsageResponse =
  | { usage?: AgentUsageStats[] | AgentUsageMap }
  | AgentUsageStats[]
  | AgentUsageMap
  | null
  | undefined;

export async function fetchAgentUsage(): Promise<AgentUsageMap> {
  const payload = await request<AgentUsageResponse>("/executive/agent-usage");
  if (!payload) return {};
  if (Array.isArray(payload)) {
    return payload.reduce<AgentUsageMap>((acc, entry) => {
      if (entry?.agent) {
        acc[entry.agent] = entry;
      }
      return acc;
    }, {});
  }
  if ("usage" in (payload as Record<string, unknown>)) {
    const usage = (payload as { usage?: AgentUsageStats[] | AgentUsageMap }).usage;
    if (Array.isArray(usage)) {
      return usage.reduce<AgentUsageMap>((acc, entry) => {
        if (entry?.agent) {
          acc[entry.agent] = entry;
        }
        return acc;
      }, {});
    }
    if (usage && typeof usage === "object") {
      return usage as AgentUsageMap;
    }
  }
  if (typeof payload === "object") {
    return payload as AgentUsageMap;
  }
  return {};
}

type MetricsResponse = { metrics?: ExecutiveMetrics } | ExecutiveMetrics | null;

export async function fetchExecutiveMetrics(): Promise<ExecutiveMetrics> {
  const payload = await request<MetricsResponse>("/executive/metrics");
  if (!payload) return null;
  if ("metrics" in (payload as Record<string, unknown>)) {
    return (payload as { metrics?: ExecutiveMetrics }).metrics ?? null;
  }
  return payload as ExecutiveMetrics;
}

type ActivityResponse =
  | { events?: ExecutiveActivityEvent[] }
  | { items?: ExecutiveActivityEvent[] }
  | ExecutiveActivityEvent[]
  | null
  | undefined;

export async function fetchExecutiveActivity(limit = 200): Promise<ExecutiveActivityEvent[]> {
  const payload = await request<ActivityResponse>(`/executive/activity?limit=${encodeURIComponent(limit)}`);
  if (Array.isArray(payload)) return payload;
  if (payload?.events && Array.isArray(payload.events)) {
    return payload.events;
  }
  if (payload?.items && Array.isArray(payload.items)) {
    return payload.items;
  }
  return [];
}
